export const INFO_PAGES = {
  account: {
    title: "My Account",
    intro: "Manage your Zebaa experience.",
    blocks: [
      { type: "para", text: "Sign in to your Zebaa account to track orders, save pieces to your wishlist and earn rewards on every purchase." },
      { type: "subheading", text: "Need help with an order right now?" },
      { type: "para", text: "Our team is always happy to help. Email us at hello@zebaa.uk and we'll assist you personally." },
      { type: "cta", label: "Contact Us", to: "/contact" },
    ],
  },
  "orders-returns": {
    title: "Orders & Returns",
    intro: "Everything you need to know about your order.",
    blocks: [
      { type: "subheading", text: "Placing an Order" },
      { type: "para", text: "Once you place an order you'll pay securely by card at checkout and receive a confirmation email with your order reference. We'll email you again with tracking as soon as your order is dispatched via Royal Mail." },
      { type: "subheading", text: "Returns" },
      { type: "list", items: [
        "Returns are accepted within 14 days of delivery.",
        "Pieces must be unworn, undamaged and in their original packaging and pouches, with all tags attached.",
        "For hygiene reasons, earrings and nose rings can only be returned if the seal on the packaging has not been opened.",
        "Items must be free from perfume, deodorant, makeup, fake tan, smoke, pet hair and any other marks or odours.",
        "Sale items are final sale unless they are faulty.",
        "Customers are responsible for return postage costs unless the item is faulty or incorrect.",
        "To start a return, email hello@zebaa.uk with your order number.",
      ]},
      { type: "subheading", text: "Return Inspection" },
      { type: "para", text: "Every returned item is carefully inspected before a refund is approved. We reserve the right to refuse returns that do not meet our returns policy. If a piece is returned in a condition that makes it unsuitable for resale, we may refuse the refund or make an appropriate deduction where permitted by UK consumer law." },
      { type: "subheading", text: "Refunds" },
      { type: "para", text: "Once your return has been received and successfully passed inspection, your refund will be issued to your original payment method within 5–7 working days." },
      { type: "subheading", text: "Faulty or Incorrect Items" },
      { type: "para", text: "If your order arrives faulty, damaged or incorrect, please contact us within 48 hours of delivery with clear photographs of the issue before the piece is worn. We'll assess the issue and arrange an appropriate resolution as quickly as possible." },
    ],
  },
  shipping: {
    title: "Shipping Information",
    intro: "Fast, tracked Royal Mail delivery across the United Kingdom.",
    blocks: [
      { type: "subheading", text: "UK Delivery - Royal Mail Tracked 48" },
      { type: "list", items: [
        "All UK orders are sent via Royal Mail Tracked 48.",
        "FREE UK delivery on all orders over £40.",
        "A flat rate of £4.99 applies to UK orders under £40.",
        "Estimated delivery: 2–3 working days from dispatch.",
        "Orders are dispatched within 1–2 working days.",
        "You'll receive a tracking number by email as soon as your order ships.",
      ]},
      { type: "subheading", text: "UK Delivery Only" },
      { type: "para", text: "Please note we currently deliver within the United Kingdom only." },
      { type: "subheading", text: "Click & Collect" },
      { type: "para", text: "Prefer to collect in person? Visit our shop at Unit 1, 312 Green Street, London, E13 9AP and pick up your order free of charge." },
    ],
  },
  "size-guide": {
    title: "Size & Fit Guide",
    intro: "Choose the right length and size for every piece.",
    blocks: [
      { type: "para", text: "Every piece at Zebaa lists its dimensions on the product page. Below are general guidelines to help you choose the right necklace length, bangle size and earring style." },
      { type: "subheading", text: "Necklace Lengths (approximate)" },
      { type: "table", headers: ["Style", "Length", "Sits At"], rows: [
        ["Choker", "14\" / 36 cm", "Base of the neck"],
        ["Princess", "18\" / 46 cm", "Collarbone"],
        ["Matinee", "22\" / 56 cm", "Just above the bust"],
        ["Rani Haar / Long", "28–36\" / 71–91 cm", "Chest / below the bust"],
      ]},
      { type: "subheading", text: "Bangle & Kada Sizes (inner diameter)" },
      { type: "table", headers: ["Size", "Inner Diameter", "Wrist Fit"], rows: [
        ["2.2", "2.20\" / 5.6 cm", "Very small"],
        ["2.4", "2.36\" / 6.0 cm", "Small"],
        ["2.6", "2.55\" / 6.5 cm", "Medium"],
        ["2.8", "2.75\" / 7.0 cm", "Large"],
        ["2.10", "2.95\" / 7.5 cm", "Extra large"],
      ]},
      { type: "para", text: "To measure: tuck your thumb into your palm and measure the widest part of your hand. Choose the bangle size that matches - you should be able to slide it on comfortably without stretching." },
      { type: "subheading", text: "Earring Fittings" },
      { type: "list", items: [
        "Pierced fittings - standard hook, post or screw-back.",
        "Some jhumkas and chandbalis include a supporting chain (saharay) that clips into your hair to take weight off the earlobe on heavier pieces.",
        "If you would like a clip-on conversion instead of a pierced fitting, message us on Instagram @zebaa.uk before ordering - this is available on selected pieces.",
      ]},
      { type: "para", text: "If you're unsure which size or length to go for, email hello@zebaa.uk with the piece you're considering and we'll help you decide." },
    ],
  },
  faqs: {
    title: "Frequently Asked Questions",
    intro: "Answers to our most common questions.",
    blocks: [
      { type: "faq", items: [
        { q: "Is your jewellery real gold or fine jewellery?", a: "No - everything at Zebaa is imitation (artificial) jewellery. That means gold-tone alloys, kundan-style settings, faux pearls and coloured stones - designed to look the part on the big day without the price tag of real gold or diamonds." },
        { q: "How long does delivery take?", a: "UK orders are sent via Royal Mail Tracked 48 and typically arrive within 2–3 working days of dispatch. Orders are dispatched within 1–2 working days." },
        { q: "Do you offer free delivery?", a: "Yes - UK delivery is free on all orders over £40. Orders under £40 are charged a flat £4.99." },
        { q: "Can I return an item?", a: "Unworn pieces in their original packaging can be returned within 14 days. For hygiene reasons, earrings and nose rings can only be returned if the packaging seal has not been opened. Sale items are final sale unless faulty." },
        { q: "How do I care for imitation jewellery?", a: "Keep pieces dry - remove before showering, swimming or exercising. Store in the pouch provided, away from direct heat. Perfume, hair spray and lotions can dull the gold-tone finish, so apply those before putting your jewellery on." },
        { q: "Can I visit your shop?", a: "Yes - visit us at Unit 1, 312 Green Street, London, E13 9AP. Mon–Sat 10am–7pm, Sun 11am–5pm." },
        { q: "How do I pay?", a: "You pay securely by debit or credit card at checkout via Stripe. Your card details are encrypted and never stored on our servers." },
      ]},
    ],
  },
  "privacy-policy": {
    title: "Privacy Policy",
    intro: "How we handle and protect your data.",
    blocks: [
      { type: "para", text: "Zebaa is committed to protecting your privacy. This policy explains what information we collect and how we use it." },
      { type: "subheading", text: "Information We Collect" },
      { type: "para", text: "We collect the details you provide when placing an order or subscribing to our newsletter - such as your name, email, phone number and delivery address." },
      { type: "subheading", text: "How We Use It" },
      { type: "list", items: [
        "To process and deliver your orders.",
        "To contact you about your order or enquiry.",
        "To send marketing emails, only where you've opted in.",
      ]},
      { type: "subheading", text: "Your Rights" },
      { type: "para", text: "You may ask us to access, correct or delete your data, or unsubscribe from marketing at any time by emailing hello@zebaa.uk. We never sell your data to third parties." },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    intro: "The terms governing use of our website and services.",
    blocks: [
      { type: "para", text: "By using the Zebaa website and placing an order, you agree to the following terms." },
      { type: "list", items: [
        "All prices are shown in GBP (£) and include VAT where applicable.",
        "All pieces sold by Zebaa are imitation (artificial) jewellery, not real gold or diamonds.",
        "Product images are representative; slight colour variation may occur due to lighting and screen calibration.",
        "Orders are subject to availability and confirmation of payment.",
        "We reserve the right to refuse or cancel any order.",
        "Returns are governed by our Orders & Returns policy.",
      ]},
      { type: "subheading", text: "Contact" },
      { type: "para", text: "Questions about these terms? Email hello@zebaa.uk or write to us at Unit 1, 312 Green Street, London, E13 9AP." },
    ],
  },
};
